<mat-card>
    <mat-card-content>

        <!-- Informacje o zamówieniu -->
        <div class="order-header" *ngIf="orderId">
            <div *ngIf="isLoadingOrder" class="loading-row">
                <mat-spinner diameter="20"></mat-spinner>
                <span>Wczytywanie danych specyfikacji...</span>
            </div>
            <h1 *ngIf="!isLoadingOrder && order" class="order-info">
                <span class="order-label">Specyfikacja:</span>
                <strong>
                    {{ (order?.orderTitle?.countBeforeHardening ?? order?.orderTitle?.count) }}/{{
                    order?.orderTitle?.year }}
                    <span *ngIf="order?.companyName"> – {{ order.companyName }}</span>
                </strong>
            </h1>
        </div>

        <form [formGroup]="form">

            <!-- Wybór języka umowy -->
            <mat-form-field appearance="fill" class="full-width language-field">
                <mat-label>Język umowy</mat-label>
                <mat-select formControlName="language" [disabled]="hasUnsavedChanges">
                    <mat-option value="PL">PL</mat-option>
                </mat-select>
            </mat-form-field>
            <ng-container *ngIf="hasUnsavedChanges">
                <mat-error class="unsaved-changes-warning">
                    Masz niezapisane zmiany. Aby zmienić język, musisz najpierw zapisać zmiany.
                </mat-error>
            </ng-container>

            <div *ngIf="isLoadingDocument" class="loading-row" style="margin-bottom: 12px;">
                <mat-spinner diameter="18"></mat-spinner>
                <span>Wczytywanie zapisanej wersji językowej...</span>
            </div>

            <div class="pdf-options-row">
                <mat-checkbox formControlName="includeDualLanguage">
                    Generuj dokument dwujęzyczny
                </mat-checkbox>
                <div style="width: 100%;" *ngIf="form.get('includeDualLanguage')?.value">
                    <mat-form-field appearance="fill" class="language-field">
                        <mat-label>Drugi język</mat-label>
                        <mat-select formControlName="secondaryLanguage">
                            <mat-option *ngFor="let lang of languages" [value]="lang.name"
                                [disabled]="lang.name === form.get('language')?.value">
                                {{ lang.name }}
                            </mat-option>
                        </mat-select>
                    </mat-form-field>
                </div>
            </div>

            <div class="variable-group"
                *ngIf="form.get('includeDualLanguage')?.value && requiredTranslationVariables.length">
                <h4 class="group-title">Pola wymagające tłumaczenia</h4>
                <div class="fields-grid">
                    <ng-container *ngFor="let variable of requiredTranslationVariables">
                        <mat-form-field *ngIf="variable.inputType === 'textarea'" appearance="fill" class="full-width">
                            <mat-label>{{ variable.label }} ({{ getSecondaryLanguageLabel() }})</mat-label>
                            <textarea matInput rows="3"
                                [formControlName]="toRequiredTranslationControlName(variable.key)"
                                [placeholder]="variable.tooltip"></textarea>
                        </mat-form-field>

                        <mat-form-field *ngIf="variable.inputType === 'number'" appearance="fill">
                            <mat-label>{{ variable.label }} ({{ getSecondaryLanguageLabel() }})</mat-label>
                            <input matInput type="number"
                                [formControlName]="toRequiredTranslationControlName(variable.key)"
                                [placeholder]="variable.tooltip" (wheel)="preventNumberScroll($event)" />
                        </mat-form-field>

                        <mat-form-field *ngIf="variable.inputType === 'date'" appearance="fill">
                            <mat-label>{{ variable.label }} ({{ getSecondaryLanguageLabel() }})</mat-label>
                            <input matInput type="date"
                                [formControlName]="toRequiredTranslationControlName(variable.key)" />
                        </mat-form-field>

                        <mat-form-field *ngIf="variable.inputType === 'select'" appearance="fill">
                            <mat-label>{{ variable.label }} ({{ getSecondaryLanguageLabel() }})</mat-label>
                            <mat-select [formControlName]="toRequiredTranslationControlName(variable.key)">
                                <mat-option *ngFor="let option of getSelectOptions(variable)" [value]="option.value">
                                    {{ option.label }}
                                </mat-option>
                            </mat-select>
                        </mat-form-field>

                        <mat-form-field *ngIf="!variable.inputType || variable.inputType === 'text'" appearance="fill">
                            <mat-label>{{ variable.label }} ({{ getSecondaryLanguageLabel() }})</mat-label>
                            <input matInput type="text"
                                [formControlName]="toRequiredTranslationControlName(variable.key)"
                                [placeholder]="variable.tooltip" />
                        </mat-form-field>
                    </ng-container>
                </div>
            </div>

            <mat-divider class="section-divider"></mat-divider>

            <!-- Pola zmiennych szablonu -->
            <div class="variables-section">
                <h3 class="section-title">Szczegóły</h3>

                <div class="variable-group" *ngIf="buyerVariables.length">
                    <h4 class="group-title">Kupujący</h4>
                    <div class="fields-grid">
                        <ng-container *ngFor="let variable of buyerVariables">
                            <ng-container *ngTemplateOutlet="variableFieldTemplate; context: { $implicit: variable }">
                            </ng-container>
                        </ng-container>
                    </div>
                </div>

                <div class="variable-group" *ngIf="sellerVariables.length">
                    <h4 class="group-title">Sprzedawca</h4>
                    <div class="fields-grid">
                        <ng-container *ngFor="let variable of sellerVariables">
                            <ng-container *ngTemplateOutlet="variableFieldTemplate; context: { $implicit: variable }">
                            </ng-container>
                        </ng-container>
                    </div>
                </div>

                <div class="variable-group" *ngIf="otherVariables.length">
                    <h4 class="group-title">Pozostałe</h4>
                    <div class="fields-grid">
                        <ng-container *ngFor="let variable of otherVariables">
                            <ng-container *ngTemplateOutlet="variableFieldTemplate; context: { $implicit: variable }">
                            </ng-container>
                        </ng-container>
                    </div>
                </div>
            </div>

            <ng-template #variableFieldTemplate let-variable>
                <!-- Podpis (ukryty, grafika przy druku) -->
                <div *ngIf="variable.hidden" class="signature-field-preview">
                    <span class="signature-label mat-body-2">{{ variable.label }}</span>
                    <img [src]="SIGNATURE_IMAGE_PATH" alt="Podpis" class="signature-preview-img"
                        [matTooltip]="variable.tooltip" />
                    <span class="signature-hint mat-caption">Grafika wstawiana automatycznie przy druku</span>
                </div>

                <!-- Pole readonly (disabled, uzupełniane automatycznie) -->
                <mat-form-field *ngIf="isVariableDisabled(variable)" appearance="fill">
                    <mat-label>
                        {{ variable.label }}
                        <span *ngIf="isOrderDerivedOverridden(variable)" class="override-badge">nadpisane</span>
                    </mat-label>
                    <input matInput type="text" [formControlName]="toControlName(variable.key)" readonly />
                </mat-form-field>

                <mat-form-field
                    *ngIf="!isVariableDisabled(variable) && !variable.hidden && variable.inputType === 'textarea'"
                    appearance="fill" class="full-width">
                    <mat-label>
                        {{ variable.label }}
                        <span *ngIf="isOrderDerivedOverridden(variable)" class="override-badge">nadpisane</span>
                    </mat-label>
                    <textarea matInput rows="3" [formControlName]="toControlName(variable.key)"
                        [placeholder]="variable.tooltip"></textarea>
                </mat-form-field>

                <!-- Pole liczbowe (np. ceny) -->
                <mat-form-field
                    *ngIf="!isVariableDisabled(variable) && !variable.hidden && variable.inputType === 'number'"
                    appearance="fill">
                    <mat-label>
                        {{ variable.label }}
                        <span *ngIf="isOrderDerivedOverridden(variable)" class="override-badge">nadpisane</span>
                    </mat-label>
                    <input matInput type="number" [formControlName]="toControlName(variable.key)"
                        [placeholder]="variable.tooltip" (wheel)="preventNumberScroll($event)" />
                </mat-form-field>

                <!-- Pole daty -->
                <mat-form-field
                    *ngIf="!isVariableDisabled(variable) && !variable.hidden && variable.inputType === 'date'"
                    appearance="fill">
                    <mat-label>
                        {{ variable.label }}
                        <span *ngIf="isOrderDerivedOverridden(variable)" class="override-badge">nadpisane</span>
                    </mat-label>
                    <input matInput type="date" [formControlName]="toControlName(variable.key)" />
                </mat-form-field>

                <!-- Waluta -->
                <mat-form-field
                    *ngIf="!isVariableDisabled(variable) && !variable.hidden && variable.inputType === 'select'"
                    appearance="fill">
                    <mat-label>
                        {{ variable.label }}
                        <span *ngIf="isOrderDerivedOverridden(variable)" class="override-badge">nadpisane</span>
                    </mat-label>
                    <mat-select [formControlName]="toControlName(variable.key)">
                        <mat-option *ngFor="let option of getSelectOptions(variable)" [value]="option.value">
                            {{ option.label }}
                        </mat-option>
                    </mat-select>
                </mat-form-field>

                <!-- Tekst (domyślnie) -->
                <mat-form-field
                    *ngIf="!isVariableDisabled(variable) && !variable.hidden && (!variable.inputType || variable.inputType === 'text')"
                    appearance="fill">
                    <mat-label>
                        {{ variable.label }}
                        <span *ngIf="isOrderDerivedOverridden(variable)" class="override-badge">nadpisane</span>
                    </mat-label>
                    <input matInput type="text" [formControlName]="toControlName(variable.key)"
                        [placeholder]="variable.tooltip" />
                </mat-form-field>
            </ng-template>

            <!-- Przyciski akcji -->
            <div class="actions-row">
                <button mat-stroked-button type="button" [disabled]="!form.get('language')?.value || isSaving"
                    (click)="saveDocument()">
                    <mat-spinner *ngIf="isSaving" diameter="16"
                        style="display:inline-block;margin-right:6px;"></mat-spinner>
                    Zapisz
                </button>

                <button mat-stroked-button type="button" [disabled]="!form.get('language')?.value"
                    (click)="previewDocument()" [color]="isPreviewVisible ? 'primary' : ''">
                    {{ isPreviewVisible ? 'Ukryj podgląd' : 'Podgląd' }}
                </button>

                <button mat-stroked-button type="button" *ngIf="canPrint" [disabled]="!form.get('language')?.value"
                    (click)="printDocument()">
                    Drukuj
                </button>

                <button style="margin-left: auto;" mat-stroked-button type="button" *ngIf="userRole === 'superadmin'"
                    [disabled]="!form.get('language')?.value || isResetting" (click)="resetDocumentTemplate()"
                    matTooltip="" color="warn">
                    <mat-spinner *ngIf="isResetting" diameter="16"
                        style="display:inline-block;margin-right:6px;"></mat-spinner>
                    Resetuj szablon
                </button>
            </div>

            <!-- Iframe podglądu dokumentu -->
            <div *ngIf="isPreviewVisible" class="preview-section">
                <mat-divider></mat-divider>
                <iframe [srcdoc]="previewHtml" class="preview-iframe" sandbox="allow-same-origin"></iframe>
            </div>
        </form>
    </mat-card-content>
</mat-card>